import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LayoutChangeEvent, Platform, Pressable, View } from 'react-native';
import { SizableText, YStack, type SizableTextProps } from 'tamagui';
import { CaptionText } from '@/components/ui/Typography';
import { BRAND_COLORS } from '@/lib/brandColors';

type Props = {
  children: string;
  numberOfLines: number;
  textProps?: Omit<SizableTextProps, 'numberOfLines' | 'ellipsizeMode'>;
  onExpandedChange?: (expanded: boolean) => void;
};

export function TruncatedTextReadMore({ children, numberOfLines, textProps = {}, onExpandedChange }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const ref = useRef<any>(null);
  const text = children.trim();

  const checkWeb = useCallback(() => {
    if (expanded) return;
    const el = ref.current as HTMLElement | null;
    if (!el) return;
    const inner = (el.querySelector('span,p,div') as HTMLElement) ?? el;
    setTruncated(inner.scrollHeight > inner.clientHeight + 1);
  }, [expanded]);

  useEffect(() => {
    if (Platform.OS !== 'web' || expanded) return;
    const raf = requestAnimationFrame(checkWeb);
    const el = ref.current as HTMLElement | null;
    const inner = el ? ((el.querySelector('span,p,div') as HTMLElement) ?? el) : null;
    if (!inner || typeof ResizeObserver === 'undefined') return () => cancelAnimationFrame(raf);
    const ro = new ResizeObserver(checkWeb);
    ro.observe(inner);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [text, numberOfLines, expanded, checkWeb]);

  const [clampedH, setClampedH] = useState(0);
  const [fullH, setFullH] = useState(0);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    if (clampedH > 0 && fullH > 0) setTruncated(fullH > clampedH + 1);
  }, [clampedH, fullH]);

  if (!text) return null;

  const toggle = () => setExpanded(prev => {
    const next = !prev;
    onExpandedChange?.(next);
    return next;
  });

  return (
    <YStack width="100%" gap={4} onLayout={(e: LayoutChangeEvent) => setWidth(e.nativeEvent.layout.width)}>

      {Platform.OS !== 'web' && !expanded && width > 0 ? (
        <View pointerEvents="none" style={{ position: 'absolute', opacity: 0, width, zIndex: -1 }}>
          <View onLayout={(e: LayoutChangeEvent) => setFullH(e.nativeEvent.layout.height)}>
            <SizableText {...textProps}>{text}</SizableText>
          </View>
        </View>
      ) : null}

      <View
        ref={ref}
        style={{ width: '100%' }}
        onLayout={Platform.OS !== 'web' ? (e: LayoutChangeEvent) => setClampedH(e.nativeEvent.layout.height) : undefined}
      >
        <SizableText
          {...textProps}
          numberOfLines={Platform.OS === 'web' || expanded ? undefined : numberOfLines}
          ellipsizeMode={Platform.OS === 'web' || expanded ? undefined : 'tail'}
          style={Platform.OS === 'web' && !expanded ? {
            display: '-webkit-box' as any,
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: numberOfLines,
            overflow: 'hidden',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            width: '100%',
          } : undefined}
        >
          {text}
        </SizableText>
      </View>

      {truncated || expanded ? (
        <Pressable
          onPress={(e: any) => {
            e?.preventDefault?.();
            e?.stopPropagation?.();
            toggle();
          }}
          {...(Platform.OS === 'web' ? {
            'data-announcement-read-more': true,
            onClick: (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
            },
          } as any : {})}
          hitSlop={8}
          style={{ alignSelf: 'flex-end' }}
        >
          <CaptionText color={BRAND_COLORS.linkAccent} letterSpacing={0} cursor="pointer">
            {expanded ? 'Read less' : 'Read more'}
          </CaptionText>
        </Pressable>
      ) : null}
    </YStack>
  );
}
